import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
  Timestamp
} from 'firebase/firestore';
import { functions, db, auth } from './init';
import type { Role } from './roles';

/*
 * Client wrappers for the classroom Cloud Functions (functions/index.js). All classroom mutations go
 * through these Admin-SDK callables; only reads (View Classroom) hit Firestore directly, scoped by
 * the classrooms security rule.
 */

export interface StudentEntry {
  uid: string;
  username: string;
}

export interface Classroom {
  id: string;
  name: string;
  createdBy: string;
  instructorIds: string[];
  students: StudentEntry[];
  joincode: string;
  joincodeExpiresAt: number; // millis
}

export interface CreatedClassroom {
  classroomId: string;
  joincode: string;
  expiresAt: number; // millis
}

export interface JoinResult {
  classroomId: string;
  name: string;
  joinedAs: 'instructor' | 'student';
}

/** Create a classroom (staff only) → { classroomId, joincode, expiresAt }. */
export async function createClassroom(name: string): Promise<CreatedClassroom> {
  const fn = httpsCallable(functions, 'createClassroom');
  const res = await fn({ name });
  return res.data as CreatedClassroom;
}

/** Join a classroom by code. Staff join as instructors, everyone else as students. */
export async function joinClassroom(joincode: string): Promise<JoinResult> {
  const fn = httpsCallable(functions, 'joinClassroom');
  const res = await fn({ joincode });
  return res.data as JoinResult;
}

/** Set/change a user's role by email (admin only). 'student' clears the role. */
export async function setUserRole(
  email: string,
  role: Role
): Promise<{ uid: string; role: Role }> {
  const fn = httpsCallable(functions, 'setUserRole');
  const res = await fn({ email, role });
  return res.data as { uid: string; role: Role };
}

/** Mint a fresh join code for a classroom (staff member / admin), invalidating the old one. */
export async function regenerateJoinCode(
  classroomId: string
): Promise<{ joincode: string; expiresAt: number }> {
  const fn = httpsCallable(functions, 'regenerateJoinCode');
  const res = await fn({ classroomId });
  return res.data as { joincode: string; expiresAt: number };
}

/**
 * Point the signed-in user's profile at another of their classrooms ("My classroom" menu). The
 * users update rule lets the owner write any field but `username`, so this needs no callable. The
 * pointer is not a security boundary: reading `classrooms/{id}` still requires isAdmin() or
 * membership in instructorIds. Callers also dispatch setCurrentClassroom() to update Redux.
 */
export async function switchCurrentClassroom(
  classroomId: string,
  name: string
): Promise<void> {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  await updateDoc(doc(db, `users/${uid}`), {
    currentClassroomId: classroomId,
    currentClassroomName: name
  });
}

function tsToMillis(value: unknown): number {
  if (value instanceof Timestamp) return value.toMillis();
  if (typeof value === 'number') return value;
  return 0;
}

function mapClassroom(id: string, data: any): Classroom {
  return {
    id,
    name: data.name,
    createdBy: data.createdBy,
    instructorIds: data.instructorIds || [],
    students: data.students || [],
    joincode: data.joincode,
    joincodeExpiresAt: tsToMillis(data.joincodeExpiresAt)
  };
}

/**
 * Read a single classroom by id. Authorized for staff by the `classrooms` read rule
 * (`isAdmin() || uid in instructorIds`) — used to resolve the current classroom's students / join
 * code without listing every classroom. Returns null if missing or not readable.
 */
export async function getClassroom(
  classroomId: string
): Promise<Classroom | null> {
  if (!classroomId) return null;
  try {
    const snap = await getDoc(doc(db, `classrooms/${classroomId}`));
    if (!snap.exists()) return null;
    return mapClassroom(snap.id, snap.data());
  } catch (e) {
    // A non-instructor (or a stale current-classroom id) hits the read rule → treat as unavailable.
    return null;
  }
}

/**
 * List the classrooms the current staff user can see (View Classroom): admins see all; instructors
 * see only classrooms they belong to (`instructorIds array-contains uid`). The classrooms rule
 * authorizes exactly these reads.
 */
export async function listMyClassrooms(isAdmin: boolean): Promise<Classroom[]> {
  const uid = auth.currentUser?.uid;
  if (!uid) return [];
  const base = collection(db, 'classrooms');
  const q = isAdmin
    ? base
    : query(base, where('instructorIds', 'array-contains', uid));
  const snap = await getDocs(q);
  const classrooms = snap.docs.map((d) => mapClassroom(d.id, d.data()));
  // Newest classrooms are most relevant; sort client-side to avoid a composite index.
  classrooms.sort((a, b) => a.name.localeCompare(b.name));
  return classrooms;
}
